import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { queryClient } from 'index';
import i18n from 'providers/i18n/i18n';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { getToastErrorMessage, LogyxError } from 'services/api/errors';
import {
  closeQuotation,
  downloadQuotationPdf,
  getSingleQuotation,
  updateQuotationLine,
} from 'services/QuotationService';
import { IUpdateQuotationLineDTO } from 'types/Quotation.types';

export const useGetSingleQuotation = (quotationId: string) =>
  useQuery({
    queryKey: [
      ReactQueryKeys.GET_SINGLE_QUOTATION,
      quotationId,
      i18n.resolvedLanguage,
    ],
    queryFn: () => getSingleQuotation(quotationId),
    enabled: !!quotationId,
  });

export const useDownloadQuotationPdf = (onSuccess?: (data: Blob) => void) =>
  useMutation((quotationId: string) => downloadQuotationPdf(quotationId), {
    onSuccess: (data: Blob) => {
      onSuccess?.(data);
    },
    onError: (error: LogyxError) => {
      toast.error(getToastErrorMessage(error));
    },
    mutationKey: ReactMutationKeys.DOWNLOAD_QUOTATION_PDF,
  });

export const useUpdateQuotationLine = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  return useMutation(
    (params: {
      quotationId: string;
      quotationLineId: string;
      dto: IUpdateQuotationLineDTO;
    }) =>
      updateQuotationLine(
        params.quotationId,
        params.quotationLineId,
        params.dto
      ),
    {
      onSuccess: () => {
        toast.success(t('Product updated successfully'));
        queryClient.invalidateQueries(ReactQueryKeys.GET_SINGLE_QUOTATION);
        queryClient.invalidateQueries(ReactQueryKeys.GET_BUYER_QUOTATIONS);
        queryClient.invalidateQueries(ReactQueryKeys.GET_QUOTATION_LINES);
        onSuccess?.();
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error), {
          toastId: ReactMutationKeys.UPDATE_QUOTATION_LINE,
        });
      },
      mutationKey: ReactMutationKeys.UPDATE_QUOTATION_LINE,
    }
  );
};

export const useCloseQuotation = () => {
  const { t } = useTranslation();
  return useMutation((quotationId: string) => closeQuotation(quotationId), {
    onSuccess: () => {
      toast.success(t('Quotation closed successfully'));
      queryClient.invalidateQueries(ReactQueryKeys.GET_SINGLE_QUOTATION);
      queryClient.invalidateQueries(ReactQueryKeys.GET_BUYER_QUOTATIONS);
    },
    onError: (error: LogyxError) => {
      toast.error(getToastErrorMessage(error), {
        toastId: ReactMutationKeys.CLOSE_QUOTATION,
      });
    },
    mutationKey: ReactMutationKeys.CLOSE_QUOTATION,
  });
};
